
import React, { useState, useEffect } from 'react';
import { useTheme } from '../src/hooks/useTheme';
import type { Wine } from '../types';
// import SommelierCatIcon from './icons/SommelierCatIcon';

// 一時的にインライン定義
const SommelierCatIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    className={className}
    aria-label="Cat Paw Icon"
    role="img"
    fill="currentColor"
  >
    <path d="M12 18.2c-1.45 2.08-4.5 2.08-5.95 0C4.6 16.12 6.55 13 9.1 13.6c.85.2 1.8.95 2.9 1.95c1.1-.99 2.05-1.75 2.9-1.95c2.55-.6 4.5 2.52 3.05 4.6c-1.45 2.08-4.5 2.08-5.95 0Z" />
    <circle cx="6.5" cy="10.5" r="2.3" />
    <circle cx="10.5" cy="8" r="2.3" />
    <circle cx="13.5" cy="8" r="2.3" />
    <circle cx="17.5" cy="10.5" r="2.3" />
  </svg>
);
import QRCodeDisplay from './QRCodeDisplay';
import SmallQRCode from './SmallQRCode';

interface WineMenuProps {
  wineList: Wine[];
  onBack: () => void;
  userId?: string;
}

const WineCard: React.FC<{ wine: Wine }> = ({ wine }) => (
    <div 
      className="backdrop-blur-sm rounded-lg shadow-md overflow-hidden flex flex-col h-full theme-bg-secondary theme-border"
      style={{
        border: `1px solid var(--theme-border)`
      }}
    >
        <div className="w-full h-32 sm:h-40 bg-gray-50/50 flex items-center justify-center p-2">
             {wine.image ? (
                <img src={wine.image} alt={wine.name} className="max-w-full max-h-full object-contain" />
            ) : (
                <SommelierCatIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 opacity-75" />
            )}
        </div>
        <div className="p-3 sm:p-4 flex flex-col flex-grow">
            <div className="flex justify-between items-start flex-wrap gap-x-2 mb-2">
                <h4 
                  className="text-base sm:text-lg font-semibold flex-1 theme-text-primary"
                >
                  {wine.name}
                </h4>
                <div className="text-right">
                    <p className="text-sm sm:text-md font-bold text-red-800 whitespace-nowrap">¥{typeof wine.price === 'number' ? wine.price.toLocaleString() : '0'}</p>
                    {wine.isGlassWine && wine.glassPrice && (
                        <div className="flex items-center justify-end gap-2 mt-2">
                            <span className="text-xs sm:text-sm font-bold text-white bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-1.5 rounded-full shadow-sm">
                                🍷 グラス
                            </span>
                            <p className="text-base sm:text-lg font-bold text-blue-800 whitespace-nowrap">
                                ¥{typeof wine.glassPrice === 'number' ? wine.glassPrice.toLocaleString() : '0'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
            
            <p className="text-xs sm:text-sm mb-2 sm:mb-3" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>{wine.type} &middot;</p>

            <div className="text-xs sm:text-sm space-y-1 flex-grow" style={{ color: 'var(--theme-text-secondary, #64748B)' }}>
                <div className="flex">
                    <span className="w-14 sm:w-16 font-semibold flex-shrink-0" style={{ color: 'var(--theme-text-primary, #1E293B)' }}>産地：</span>
                    <span className="flex-1">{wine.origin}</span>
                </div>
                <div className="flex">
                    <span className="w-14 sm:w-16 font-semibold text-gray-800 flex-shrink-0">品種：</span>
                    <span className="flex-1">{wine.variety}</span>
                </div>
                <div className="flex">
                    <span className="w-14 sm:w-16 font-semibold text-gray-800 flex-shrink-0">特徴：</span>
                    <span className="flex-1">{Array.isArray(wine.characteristics) ? wine.characteristics.join(', ') : String(wine.characteristics || '')}</span>
                </div>
            </div>
        </div>
    </div>
);

const WineMenu: React.FC<WineMenuProps> = ({ wineList, onBack, userId }) => {
  // テーマの状態管理
  const { theme: currentThemeId } = useTheme();

  // テーマの変更は useTheme フックで自動的に処理される
  
  // デバッグ用ログ
  console.log('=== WineMenu テーマデバッグ情報 ===');
  console.log('Current theme ID:', currentThemeId);
  console.log('==================================');
  
  const visibleWines = wineList.filter(wine => wine.visible);
  
  const winesByType = visibleWines.reduce((acc, wine) => {
    const type = wine.type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(wine);
    return acc;
  }, {} as Record<Wine['type'], Wine[]>);

  const typeOrder: Wine['type'][] = ['スパークリング', 'ロゼ', '白', '赤'];


  // ユーザー別ワインリストのURLを生成
  const wineListUrl = (() => {
    console.log('=== WineMenu URL生成デバッグ ===');
    console.log('userId prop:', userId);
    console.log('userId type:', typeof userId);
    console.log('userId存在:', !!userId);
    console.log('window.location.href:', window.location.href);
    console.log('window.location.origin:', window.location.origin);
    
    if (userId) {
      const baseUrl = window.location.origin;
      // 管理者ページと同じ動的表示URLを使用
      const dynamicUrl = `${baseUrl}/public-wine-list?user=${encodeURIComponent(userId)}`;
      console.log('encodeURIComponent(userId):', encodeURIComponent(userId));
      console.log('生成された動的URL:', dynamicUrl);
      return dynamicUrl;
    } else {
      console.log('userId が未設定のため、メインページのURLを使用');
      console.log('メインページのURL:', window.location.origin);
      // メインページの場合は、現在のページのURLを使用
      return window.location.origin;
    }
  })();

  // テーマに基づくスタイルを計算
  const getThemeStyles = () => {
    const themeConfig = {
      modern: {
        bgPrimary: '#F8FAFC',
        bgSecondary: '#FFFFFF',
        textPrimary: '#1E293B',
        accentPrimary: '#2563EB'
      },
      gorgeous: {
        bgPrimary: '#1A1A2E',
        bgSecondary: '#16213E',
        textPrimary: '#E94560',
        accentPrimary: '#E94560'
      },
      minimal: {
        bgPrimary: '#FFFFFF',
        bgSecondary: '#F8F9FA',
        textPrimary: '#2D3748',
        accentPrimary: '#4A5568'
      },
      resort: {
        bgPrimary: '#E6FFFA',
        bgSecondary: '#B2F5EA',
        textPrimary: '#234E52',
        accentPrimary: '#38B2AC'
      },
      vintage: {
        bgPrimary: '#F7F3E9',
        bgSecondary: '#E8D5B7',
        textPrimary: '#8B4513',
        accentPrimary: '#D2691E'
      }
    };
    
    const theme = themeConfig[currentThemeId as keyof typeof themeConfig] || themeConfig.modern;
    return theme;
  };

  const themeStyles = getThemeStyles();

  return (
    <div 
      className={`w-full max-w-4xl mx-2 min-h-screen p-4 theme-bg-primary theme-text-primary theme-${currentThemeId}`}
      style={{ 
        border: `3px solid ${themeStyles.accentPrimary}`,
        borderRadius: '10px',
        backgroundColor: themeStyles.bgPrimary,
        color: themeStyles.textPrimary
      }}
    >
      <div className="text-center mb-6 sm:mb-10">
        <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 mb-2">
            <div className="flex items-center gap-3">
              <h2 
                className="text-2xl sm:text-3xl md:text-4xl font-bold theme-text-primary"
              >
                Wine List
              </h2>
              <SmallQRCode url={wineListUrl} size={80} />
            </div>
        </div>
        <p 
          className="text-base sm:text-lg theme-text-primary"
          style={{ opacity: 0.8 }}
        >
          当店のワインリストです。ごゆっくりお選びください。
        </p>
        
        {/* デバッグ用テーマ表示 */}
        <div 
          className="mt-4 p-3 rounded-lg"
          style={{ 
            border: `1px solid ${themeStyles.accentPrimary}`,
            backgroundColor: `${themeStyles.accentPrimary}20`,
            color: themeStyles.textPrimary
          }}
        >
          <p className="text-sm font-bold">現在のテーマ: {currentThemeId}</p>
          <p className="text-xs mt-1">
            背景: <span style={{ color: themeStyles.bgPrimary, backgroundColor: themeStyles.bgPrimary, padding: '2px 8px', borderRadius: '3px' }}>■</span> {themeStyles.bgPrimary}
          </p>
          <p className="text-xs mt-1">
            文字: <span style={{ color: themeStyles.textPrimary, backgroundColor: themeStyles.textPrimary, padding: '2px 8px', borderRadius: '3px' }}>■</span> {themeStyles.textPrimary}
          </p>
          <p className="text-xs mt-1">
            アクセント: <span style={{ color: themeStyles.accentPrimary, backgroundColor: themeStyles.accentPrimary, padding: '2px 8px', borderRadius: '3px' }}>■</span> {themeStyles.accentPrimary}
          </p>
        </div>
        
        {/* 大きなQRコード表示は非表示（小さなQRコードをヘッダーに表示） */}
        {/*
        <div className="mt-6 flex justify-center">
          <QRCodeDisplay 
            url={wineListUrl} 
            title={userId ? "このワインリストのQRコード" : "スマホでアクセス"} 
          />
        </div>
        */}
      </div>

      {visibleWines.length > 0 ? (
        <div className="space-y-8 sm:space-y-12">
            {typeOrder.map(type => (
                winesByType[type] && winesByType[type].length > 0 && (
                    <section key={type} aria-labelledby={`wine-type-${type}`}>
                               <h3 
                                 id={`wine-type-${type}`} 
                                 className="text-xl sm:text-2xl font-bold border-b-2 pb-2 mb-4 sm:mb-6 theme-accent-primary"
                                 style={{ 
                                   borderBottomColor: `var(--theme-accent-primary)40`
                                 }}
                               >
                                 {type}
                               </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                            {winesByType[type].map(wine => (
                                <WineCard key={wine.id} wine={wine} />
                            ))}
                        </div>
                    </section>
                )
            ))}
        </div>
      ) : (
        <div className="text-center bg-white/50 p-4 sm:p-8 rounded-lg">
          <p className="text-sm sm:text-base" style={{ color: 'var(--theme-text-primary, #374151)' }}>現在、表示できるワインがありません。</p>
        </div>
      )}

        <div className="text-center mt-8 sm:mt-12">
          <button
            onClick={onBack}
            className="w-full sm:w-auto font-bold py-3 px-6 sm:px-8 rounded-lg shadow-lg transition-colors duration-300 transform hover:scale-105 text-sm sm:text-base theme-button-primary"
          >
            トップに戻る
          </button>
        </div>

      {/* フッター */}
      <div 
        className="text-center mt-8 text-sm theme-text-primary"
        style={{ opacity: 0.7 }}
      >
        <p>最終更新: {new Date().toLocaleString('ja-JP')}</p>
        <p className="mt-2">
          <span style={{ opacity: 0.6 }}>Powered by</span> 
          <span 
            className="ml-1 font-semibold theme-accent-primary"
          >
            Orange Concept Sommelier Lab
          </span>
        </p>
      </div>
    </div>
  );
};

export default WineMenu;
